import {useEffect, useState, type FormEvent} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PersonaLicenciaDialog, RosterActivationConfirms} from '@/components/roster'
import {PartnerCreateDialog} from '@/components/partner-create-dialog'
import {QuotaMatrixInput} from '@/components/partner-quota-matrix'
import {
  PARTNERS_PAGE_SIZE,
  mintPartnerLicenciaUploadTarget,
  usePartnerLicenciaMutations,
  usePartners,
  useDeactivatePartner,
  useReactivatePartner,
  useUpdatePartnerQuota,
  type Partner,
} from '@/hooks/inci/aliados/use-partners'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useViewAsPartner} from '@/hooks/platform/use-view-as-partner'
import {useTableState} from '@/hooks/common/use-table-state'
import {PageHeading} from '@/components/page-heading'
import {HelpIcon} from '@/components/help-icon'
import {NoAccessState} from '@/components/no-access-state'
import {DataTable, type Column} from '@/components/data-table'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {translateApiError} from '@/lib/errors'
import {FieldError} from '@/components/field-error'
import {SearchInput} from '@/components/ui/search-input'

function licenciaTone(estado: Partner['licencia']['estado']): 'green' | 'red' | 'amber' | 'neutral' {
  if (estado === 'verificada') return 'green'
  if (estado === 'rechazada') return 'red'
  if (estado === 'por_verificar') return 'amber'
  return 'neutral'
}

function LicenciaBadge({licencia}: {licencia: Partner['licencia']}) {
  const {t} = useTranslation()
  const key = licencia.estado ?? 'none'
  return <Tag tone={licenciaTone(licencia.estado)}>{t(`partners.licenciaEstado.${key}`)}</Tag>
}

function CarteraCell({partner}: {partner: Partner}) {
  const {t} = useTranslation()
  const {empresas_total, empresas_paid, quota} = partner.cartera
  const over = empresas_paid > quota
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-xs text-foreground">
        {t('partners.cartera.paidOfQuota', {paid: empresas_paid, quota})}
      </span>
      <span className="text-[11px] text-muted-foreground">
        {t('partners.cartera.total', {total: empresas_total})}
        {over && <span className="ml-1 text-destructive">· {t('partners.cartera.over')}</span>}
      </span>
    </div>
  )
}

/** Revalida en la frontera de la mutación aunque las celdas de `QuotaMatrixInput` ya acoten cada tecla. */
function validateQuotas(quotas: Record<string, number>): string | null {
  return Object.values(quotas).every((n) => Number.isInteger(n) && n >= 0) ? null : 'errors.quotaInvalid'
}

function QuotaDialog({partner, onClose}: {partner: Partner | null; onClose: () => void}) {
  const {t} = useTranslation()
  const update = useUpdatePartnerQuota()
  const [quotas, setQuotas] = useState<Record<string, number>>({})
  const [quotaError, setQuotaError] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  // El popover de ayuda se porta al `DialogContent`, no al body: ver `ui/popover.tsx`.
  const [dialogo, setDialogo] = useState<HTMLElement | null>(null)

  // El diálogo se abre desde fuera, y el `onOpenChange` de Radix no dispara en una apertura externa.
  useEffect(() => {
    if (partner) {
      setQuotas(partner.quotas)
      setQuotaError(undefined)
    }
  }, [partner])

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!partner) return
    const problem = validateQuotas(quotas)
    setQuotaError(problem ?? undefined)
    if (problem) return
    setError(null)
    update.mutate(
      {userId: partner.user_id, quotas},
      {onSuccess: onClose, onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog
      open={!!partner}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}>
      <DialogContent ref={setDialogo} data-testid="partner-quota-dialog">
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>
              {t('partners.quotaDialog.title', {name: partner ? `${partner.name} ${partner.last_name}` : ''})}
            </DialogTitle>
            <HelpIcon label={t('partners.quotaDialog.tipLabel')} testId="partner-quota-tip" contenedor={dialogo}>
              {t('partners.quotaDialog.description')}
            </HelpIcon>
          </div>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          {partner && (
            <p className="text-xs text-muted-foreground">
              {t('partners.quotaDialog.current', {
                paid: partner.cartera.empresas_paid,
                total: partner.cartera.empresas_total,
              })}
            </p>
          )}
          <QuotaMatrixInput value={quotas} onChange={setQuotas} idPrefix="partner-quota-edit" />
          <FieldError error={quotaError} />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" data-testid="partner-quota-submit" disabled={update.isPending}>
              {t('partners.quotaDialog.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function LicenciaDialog({partner, onClose}: {partner: Partner | null; onClose: () => void}) {
  const {save, verify} = usePartnerLicenciaMutations()
  return (
    <PersonaLicenciaDialog
      target={
        partner
          ? {userId: partner.user_id, name: `${partner.name} ${partner.last_name}`, licencia: partner.licencia}
          : null
      }
      tKey="partners.licenciaDialog"
      testPrefix="partner"
      onClose={onClose}
      mintUploadTarget={mintPartnerLicenciaUploadTarget}
      onSave={(input) => save.mutateAsync(input)}
      onVerify={(userId) => verify.mutateAsync(userId).then(onClose)}
      saving={save.isPending}
      verifying={verify.isPending}
    />
  )
}

function PartnerActions({
  partner,
  onEnterPanel,
  onQuota,
  onLicencia,
  onMembers,
  onDeactivate,
  onReactivate,
  testIdPrefix = 'partner',
}: {
  partner: Partner
  onEnterPanel: (p: Partner) => void
  onQuota: (p: Partner) => void
  onLicencia: (p: Partner) => void
  onMembers: (p: Partner) => void
  onDeactivate: (p: Partner) => void
  onReactivate: (p: Partner) => void
  // Tarjetas y tabla se montan a la vez (corte por CSS): el prefijo mantiene únicos los testids.
  testIdPrefix?: string
}) {
  const {t} = useTranslation()
  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      {partner.active && (
        <Button
          size="sm"
          variant="ghost"
          data-testid={`${testIdPrefix}-enter-${partner.user_id}`}
          onClick={() => onEnterPanel(partner)}>
          {t('partners.actions.enterPanel')}
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon-xs"
            variant="ghost"
            data-testid={`${testIdPrefix}-more-${partner.user_id}`}
            aria-label={t('partners.actions.more')}>
            <span aria-hidden="true" className="text-base leading-none">
              ⋯
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            data-testid={`${testIdPrefix}-members-${partner.user_id}`}
            onSelect={() => onMembers(partner)}>
            {t('partners.actions.members')}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`${testIdPrefix}-quota-${partner.user_id}`} onSelect={() => onQuota(partner)}>
            {t('partners.actions.quota')}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`${testIdPrefix}-licencia-${partner.user_id}`}
            onSelect={() => onLicencia(partner)}>
            {t('partners.actions.licencia')}
          </DropdownMenuItem>
          {partner.active ? (
            <DropdownMenuItem
              data-testid={`${testIdPrefix}-deactivate-${partner.user_id}`}
              onSelect={() => onDeactivate(partner)}
              className="text-destructive focus:text-destructive">
              {t('partners.actions.deactivate')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              data-testid={`${testIdPrefix}-reactivate-${partner.user_id}`}
              onSelect={() => onReactivate(partner)}>
              {t('partners.actions.reactivate')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export function PartnersPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {viewAsPartner} = useViewAsPartner()
  const isSystemUser = useIsSystemUser()
  const table = useTableState('name')
  const {data, isLoading} = usePartners({page: table.page, search: table.search}, isSystemUser)
  const deactivate = useDeactivatePartner()
  const reactivate = useReactivatePartner()
  const [creating, setCreating] = useState(false)
  const [quotaTarget, setQuotaTarget] = useState<Partner | null>(null)
  const [licenciaTarget, setLicenciaTarget] = useState<Partner | null>(null)
  const [deactivateTarget, setDeactivateTarget] = useState<Partner | null>(null)
  const [reactivateTarget, setReactivateTarget] = useState<Partner | null>(null)

  if (!isSystemUser) return <NoAccessState />

  function enterPanel(p: Partner) {
    viewAsPartner({
      userId: p.user_id,
      name: p.tipo === 'juridica' ? (p.razon_social ?? '') : `${p.name} ${p.last_name}`.trim(),
    })
    navigate('/partner')
  }

  function openDetail(p: Partner) {
    navigate(`/admin/partners/${p.user_id}`)
  }

  const handlers = {
    onEnterPanel: enterPanel,
    onQuota: setQuotaTarget,
    onLicencia: setLicenciaTarget,
    onMembers: openDetail,
    onDeactivate: setDeactivateTarget,
    onReactivate: setReactivateTarget,
  }

  const columns: Column<Partner>[] = [
    {
      key: 'name',
      header: t('partners.table.name'),
      cell: (p) => (
        <CeldaEstado
          nombre={p.tipo === 'juridica' ? p.razon_social : `${p.name} ${p.last_name}`}
          chip={p.tipo === 'juridica' ? <Tag tone="ink">{t('partners.tipo.juridica')}</Tag> : undefined}
          meta={<span className="font-mono">{p.tipo === 'juridica' ? p.nit : p.cedula}</span>}
        />
      ),
    },
    {
      key: 'code',
      header: t('partners.table.code'),
      width: 'w-32',
      cell: (p) => <span className="font-mono text-xs tracking-wider text-foreground">{p.code}</span>,
    },
    {
      key: 'cartera',
      header: t('partners.table.cartera'),
      width: 'w-40',
      cell: (p) => <CarteraCell partner={p} />,
    },
    {
      key: 'licencia',
      header: t('partners.table.licencia'),
      width: 'w-32',
      cell: (p) => <CeldaEstado chip={<LicenciaBadge licencia={p.licencia} />} />,
    },
    {
      key: 'estado',
      header: t('partners.table.estado'),
      width: 'w-40',
      cell: (p) => (
        <CeldaEstado
          chip={
            <>
              <Tag tone="neutral">{t(`partners.estado.${p.active ? 'active' : 'inactive'}`)}</Tag>
              {!p.tiene_acceso && p.active && <Tag tone="amber">{t('partners.acceso.pending')}</Tag>}
            </>
          }
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-64',
      className: 'text-right',
      cell: (p) => (
        <div onClick={(e) => e.stopPropagation()}>
          <PartnerActions partner={p} {...handlers} />
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-5" data-testid="partners-page">
      <PageHeading
        title={t('partners.title')}
        ayuda={
          <HelpIcon label={t('partners.tipLabel')} testId="partners-tip">
            {t('partners.tip')}
          </HelpIcon>
        }
        accion={
          <Button size="sm" data-testid="partner-new" onClick={() => setCreating(true)}>
            {t('partners.new')}
          </Button>
        }
      />

      {isLoading && !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <ListCard
          toolbar={
            <SearchInput
              value={table.searchInput}
              onChange={table.setSearchInput}
              placeholder={t('partners.searchPlaceholder')}
            />
          }
          footer={
            !!data?.total &&
            data.items.length > 0 && (
              <PaginationBar
                page={table.page}
                pageSize={PARTNERS_PAGE_SIZE}
                total={data.total}
                onPageChange={table.setPage}
              />
            )
          }>
          {data && data.items.length === 0 ? (
            <ListEmptyState page title={table.search ? t('partners.noResults') : t('partners.empty')} />
          ) : (
            <>
              <div className="hidden lg:block">
                <DataTable columns={columns} rows={data?.items ?? []} rowKey={(p) => p.user_id} />
              </div>
              <ul className="px-3 lg:hidden">
                {data?.items.map((p) => (
                  <li
                    key={p.user_id}
                    className="surface-divider border-b py-3 last:border-0"
                    data-testid={`partner-card-${p.user_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="truncate font-medium text-foreground">
                            {p.tipo === 'juridica' ? p.razon_social : `${p.name} ${p.last_name}`}
                          </p>
                          {p.tipo === 'juridica' && <Tag tone="ink">{t('partners.tipo.juridica')}</Tag>}
                        </div>
                        <p className="truncate font-mono text-xs tracking-wider text-muted-foreground">{p.code}</p>
                      </div>
                      <Tag tone="neutral">{t(`partners.estado.${p.active ? 'active' : 'inactive'}`)}</Tag>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <LicenciaBadge licencia={p.licencia} />
                      <CarteraCell partner={p} />
                    </div>
                    <div className="mt-2">
                      <PartnerActions partner={p} {...handlers} testIdPrefix="partner-card" />
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </ListCard>
      )}

      <PartnerCreateDialog open={creating} onClose={() => setCreating(false)} />
      <QuotaDialog partner={quotaTarget} onClose={() => setQuotaTarget(null)} />
      <LicenciaDialog partner={licenciaTarget} onClose={() => setLicenciaTarget(null)} />
      <RosterActivationConfirms
        tKey="partners"
        deactivateTarget={deactivateTarget}
        reactivateTarget={reactivateTarget}
        onCloseDeactivate={() => setDeactivateTarget(null)}
        onCloseReactivate={() => setReactivateTarget(null)}
        deactivate={deactivate}
        reactivate={reactivate}
      />
    </div>
  )
}
